import { Categories } from '../../api/graphql/master/types';
import { Product, Products } from '../../api/graphql/products/types';
import { PlanGroupPlanType, PlanGroupsStateType } from '../../types/api/PlanGroupApiType';
import { SubscriptionLineApiType } from '../../types/api/SubscriptionApiType';
import { BoxSettingResult, BoxSettingResults, BoxSettingsSatisfied } from '../boxSettings';
import { CategoryContext, checkConditions } from '../categories';
import {
  calcDiscountRate,
  getNextOrderNumberForPricing,
  getSubscriptionPlanPrice,
  PickSubscription,
} from './calcPlanPrice';
import {
  SubscribableCategory,
  SubscriptionBoxProduct,
  SubscriptionProductItem,
  SubscriptionProducts,
  SubscriptionProductViewItem,
} from './types';
import { extractProductId } from './utils';

export function buildSubscriptionProducts(
  pickSub: PickSubscription,
  products: Products,
  planGroups: PlanGroupsStateType,
  cats: Categories,
  bss: BoxSettingsSatisfied,
  ctx: CategoryContext
) {
  // 1.定期登録済み商品
  const subscribed = buildSubscribedProducts(pickSub, products, planGroups, bss.active);

  // 2.定期登録可能商品
  const subscribable = buildSubscribableProducts(pickSub, products, planGroups, cats, bss, ctx);

  return {
    subscribedProducts: subscribed,
    subscribableProducts: subscribable,
  };
}

function buildSubscribableProduct(
  subscription: PickSubscription,
  product: Product,
  planGroup: PlanGroupPlanType[],
  ex?: { category: string | undefined; shortName: string | undefined }
): SubscriptionProductItem | null {
  const planPrice = getSubscriptionPlanPrice(subscription, Number(product.originPrice), planGroup);
  const nextOrder = getNextOrderNumberForPricing(subscription);
  // 2 回目のお届けのみ商品メタ discount_rate（HTML）を % 表示に使う。それ以外は空にし StaticSubscriptionProduct の PriceBox が discountPercent（プラン計算）を使う。
  const useMetafieldDiscountHtml =
    nextOrder === 2 && Boolean(product.discountRate?.trim());

  // 請求サイクルとプランが一致しない等でプランが取れない商品は一覧に出さない
  if (!planPrice) return null;

  return {
    ...product,
    ...planPrice,
    discountRate: useMetafieldDiscountHtml ? product.discountRate : '',
    lineId: '',
    quantity: 0,
    category: ex?.category ?? '',
    shortName: ex?.shortName ?? '',
  };
}

function buildSubscribedProduct(
  line: SubscriptionLineApiType,
  product: Product
): SubscriptionProductItem {
  return {
    ...product,
    currentPriceAmount: line.currentPriceAmount,
    quantity: line.quantity,
    lineId: line.lineId,
    sellingPlanId: line.sellingPlanId,
    sku: line.sku,
    discountPercent: calcDiscountRate(product.originPrice, line.currentPriceAmount),
    // 契約中は定期 API のライン（実請求単価）から discountPercent を表示したい。…product に含まれるメタの discount_rate を残すと
    // PriceBox が HTML を優先してしまい、定期登録可能側だけ直る・契約中は 99% のまま、といった不整合になるため空にする。
    discountRate: '',
  };
}

function buildBoxProduct(
  subscription: PickSubscription,
  products: Products,
  planGroups: PlanGroupsStateType,
  bs: BoxSettingResult
): SubscriptionBoxProduct | null {
  const productsInCategory: SubscriptionProductItem[] = [];
  const { setting, productIds, satisfied } = bs;
  const { productId, boxes } = setting;

  const parentProduct = products[productId];
  if (!parentProduct) return null;

  for (let i = 0; i < boxes.length; i++) {
    const { productId: id, planGroupId, shortName } = boxes[i];
    const product = products[id];
    if (!product) continue;

    const planGroup = planGroups[planGroupId];
    if (!planGroup) continue;

    const subscribed = productIds[id];
    const p =
      satisfied && subscribed
        ? buildSubscribedProduct(subscribed, product)
        : buildSubscribableProduct(subscription, product, planGroup);
    if (p) {
      productsInCategory.push({ ...p, title: shortName ?? p.title });
    }
  }

  if (productsInCategory.length === 0) return null;

  return { parent: { ...parentProduct, ...setting }, items: productsInCategory };
}

function buildSubscribedProducts(
  subscription: PickSubscription,
  products: Products,
  planGroups: PlanGroupsStateType,
  activeBss: BoxSettingResults
): SubscriptionProductViewItem[] {
  const lines = subscription.subscriptionLines;
  const result: SubscriptionProductViewItem[] = [];

  if (!Object.keys(activeBss).length) {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const id = extractProductId(line.productId);
      if (!id) continue;
      const product = products[id];
      if (!product) continue;

      const p = buildSubscribedProduct(line, product);
      result.push({ type: 'product', product: p });
    }
    return result;
  }

  const allIds: Record<string, boolean> = {};
  for (const key in activeBss) {
    const bs = activeBss[key];
    if (!bs) continue;
    for (const id in bs.productIds) {
      allIds[id] = true;
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const id = extractProductId(line.productId);
    if (!id) continue;

    const product = products[id];
    if (!product || allIds[id]) continue;

    const p = buildSubscribedProduct(line, product);
    result.push({ type: 'product', product: p });
  }

  for (const key in activeBss) {
    const bs = activeBss[key];
    if (!bs) continue;

    const box = buildBoxProduct(subscription, products, planGroups, bs);
    if (!box) continue;

    result.push({ type: 'box', box });
  }

  return result;
}

function buildSubscribableProducts(
  subscription: PickSubscription,
  products: Products,
  planGroups: PlanGroupsStateType,
  categories: Categories,
  satisfiedBss: BoxSettingsSatisfied,
  ctx: CategoryContext
) {
  const productResult: SubscriptionProducts = {};
  const categoryResult: SubscribableCategory = {};

  const subscribedProduct: Record<string, boolean> = {};
  const lines = subscription.subscriptionLines;
  for (let i = 0; i < lines.length; i++) {
    const productId = lines[i].productId;
    const id = extractProductId(productId) ?? productId;
    subscribedProduct[id] = true;
  }

  const { active, passive } = satisfiedBss;
  for (const categoryName in categories) {
    const c = categories[categoryName];
    if (!checkConditions(c.conditions, ctx)) continue;

    const productsInCategory: SubscriptionProductViewItem[] = [];
    const list = c.productList;

    for (let i = 0; i < list.length; i++) {
      const { productId: id, planGroupId, category, shortName } = list[i];
      if (subscribedProduct[id]) continue;
      if (active[id]) continue;

      const product = products[id];
      if (!product) continue;

      if (!planGroupId) {
        const bs = passive[id];
        if (!bs) continue;

        const box = buildBoxProduct(subscription, products, planGroups, bs);

        if (!box) continue;

        productsInCategory.push({ type: 'box', box });
      } else {
        const planGroup = planGroups[planGroupId];
        if (!planGroup) continue;

        const ex = { category, shortName };
        const p = buildSubscribableProduct(subscription, product, planGroup, ex);
        if (!p) continue;

        productsInCategory.push({ type: 'product', product: p });
      }
    }

    if (productsInCategory.length !== 0) {
      productResult[categoryName] = productsInCategory;
      categoryResult[categoryName] = {
        categoryId: c.categoryId ?? '',
        categoryHtml: c.categoryHtml ?? categoryName,
        categoryImage: c.categoryImage ?? '',
        categoryName,
        column: c.column ?? 2,
      };
    }
  }

  return { category: categoryResult, products: productResult };
}

export function buildProductRecord(
  subscribed: SubscriptionProductViewItem[],
  subscribable: SubscriptionProducts
) {
  const record: Record<string, SubscriptionProductItem> = {};

  // subscribed
  for (let i = 0; i < subscribed.length; i++) {
    const item = subscribed[i];

    if (item.type === 'product') {
      const p = item.product;
      record[p.id] = p;
    } else {
      const boxItems = item.box.items;
      for (let j = 0; j < boxItems.length; j++) {
        const p = boxItems[j];
        record[p.id] = p;
      }
    }
  }

  // subscribable
  for (const key in subscribable) {
    const list = subscribable[key];
    for (let i = 0; i < list.length; i++) {
      const item = list[i];

      if (item.type === 'product') {
        const p = item.product;
        record[p.id] = p;
      } else {
        const boxItems = item.box.items;
        for (let j = 0; j < boxItems.length; j++) {
          const p = boxItems[j];
          record[p.id] = p;
        }
      }
    }
  }

  return record;
}
